"use client";

import type {
  WingmanRequestFailure,
  WingmanRequestFailureHook,
  WingmanRouting,
} from "@/services/wingman";
import { getBrowserPostHog } from "@/lib/posthog-browser";

/**
 * PostHog event name for Wingman request failures.
 *
 * Generic on purpose — `WingmanClient` is shared across products (GailGPT,
 * workflow chat, conversations). Each construction site sets `properties.product`
 * via `makeWingmanPosthogFailureHook(product)` so failures can be filtered by
 * product downstream.
 */
export const WINGMAN_FAILURE_EVENT = "wingman_request_failed";
export const WINGMAN_CONVERSATION_FLOW_EVENT = "wingman_conversation_flow";

export function captureWingmanConversationFlow({
  action,
  conversationId,
  routing,
}: {
  action: "create" | "load" | "send" | "discard_session";
  conversationId?: string;
  routing?: WingmanRouting;
}): void {
  if (typeof window === "undefined") return;
  try {
    getBrowserPostHog()?.capture(WINGMAN_CONVERSATION_FLOW_EVENT, {
      action,
      conversation_id: conversationId,
      api_version: routing?.apiVersion,
    });
  } catch (e) {
    console.warn("[wingman_tracing] capture failed", e);
  }
}

/**
 * Returns a `WingmanRequestFailureHook` that reports each failure to PostHog
 * tagged with the given `product`.
 *
 * Browser-only — server-side callers (Slack handlers) should not pass this hook
 * to `WingmanClient`. The `typeof window` guard keeps it safe if a hook is
 * accidentally wired server-side, but the proper place for that decision is the
 * construction site, not this module.
 *
 * Tracing must never break a chat send: any reporting failure logs to console
 * and is otherwise swallowed.
 */
export function makeWingmanPosthogFailureHook(
  product: string
): WingmanRequestFailureHook {
  return (failure: WingmanRequestFailure) => {
    if (typeof window === "undefined") return;
    try {
      getBrowserPostHog()?.capture(WINGMAN_FAILURE_EVENT, {
        product,
        kind: failure.kind,
        status_code: failure.status,
        status_category: failure.category,
        url: failure.url,
        method: failure.method,
        response_body: failure.responseBody,
        api_version: failure.apiVersion,
      });
    } catch (e) {
      console.warn("[wingman_tracing] capture failed", e);
    }
  };
}
